import { Target, Eye, TrendingUp, Award, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { useLanguage } from "../context/LanguageContext";

export function AboutSection() {
  const { t } = useLanguage();
  // Milestone data for the timeline
  const milestones = [
    { year: "2024", title: "Company Founded", desc: "Started with a vision to modernize agriculture" },
    { year: "2024", title: "50+ Farms", desc: "Successfully developed and transformed farms" },
    { year: "2024", title: "150+ Acres", desc: "Acres under professional management" },
    { year: "2025", title: "50+ Farms", desc: "Milestone achievement in farm development" }
  ];

  return (
    <section id="about" className="py-32 px-6 lg:px-20 bg-gradient-to-b from-white via-[#DAF1DE]/20 to-white relative overflow-hidden">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #235347 1px, transparent 1px),
            linear-gradient(to bottom, #235347 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8EB69B]/20 to-[#235347]/20 px-6 py-3 rounded-full border border-[#235347]/20 mb-6 backdrop-blur-sm"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="h-4 w-4 text-[#235347]" />
            </motion.div>
            <p className="text-[#163832] tracking-wider uppercase text-sm">{t('about.title')}</p>
          </motion.div>
          
          <motion.h2 
            className="text-5xl lg:text-6xl text-[#051F20] mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('about.title')}
          </motion.h2>
        </div>

        {/* Mission & Vision with 3D Cards - Side by Side */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {(() => {
            const mission = { title: t('about.mission.title'), text: t('about.mission.text') };
            const vision = { title: t('about.vision.title'), text: t('about.vision.text') };
            const items = [
              { icon: Target, title: mission.title, text: mission.text, gradient: 'from-[#8EB69B] to-[#235347]' },
              { icon: Eye, title: vision.title, text: vision.text, gradient: 'from-[#235347] to-[#163832]' }
            ];
            return items.map((item, idx) => (
              item
            ));
          })().map((item: any, idx: number) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: idx === 0 ? -50 : 50, rotateY: idx === 0 ? -15 : 15 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: idx * 0.2 }}
              whileHover={{ 
                y: -10,
                rotateX: 5,
                rotateY: idx === 0 ? 5 : -5,
                scale: 1.02
              }}
              style={{ 
                perspective: 1500,
                transformStyle: 'preserve-3d'
              }}
              className="relative bg-white p-8 rounded-3xl border-2 border-[#8EB69B]/10 hover:border-[#8EB69B]/40 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              {/* 3D Background Layer */}
              <div 
                className="absolute inset-0 bg-gradient-to-br from-[#8EB69B]/5 to-transparent rounded-3xl"
                style={{ transform: 'translateZ(-10px)' }}
              />
              
              {/* Animated Border Glow */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r opacity-0 group-hover:opacity-20 rounded-3xl blur-xl"
                style={{ background: `linear-gradient(to right, ${item.gradient})` }}
                animate={{
                  opacity: [0, 0.3, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity
                }}
              />
              
              <div className="relative" style={{ transform: 'translateZ(20px)' }}>
                {/* Icon */}
                <motion.div 
                  className={`h-14 w-14 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-5 shadow-lg`}
                  whileHover={{ 
                    rotate: 360,
                    scale: 1.1
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <item.icon className="h-7 w-7 text-white" />
                </motion.div>
                
                {/* Title */}
                <h3 className="text-2xl text-[#051F20] mb-4">
                  {item.title}
                </h3>
                
                {/* Description */}
                <p className="text-[#163832]/80 leading-relaxed">
                  {item.text}
                </p>
              </div>
              
              {/* Corner Accents */}
              <div className="absolute top-4 right-4 w-12 h-12 border-t-2 border-r-2 border-[#8EB69B]/20 rounded-tr-2xl"></div>
              <div className="absolute bottom-4 left-4 w-12 h-12 border-b-2 border-l-2 border-[#8EB69B]/20 rounded-bl-2xl"></div>
            </motion.div>
          ))}
        </div>

        {/* Journey Timeline */}
        <div className="mb-20">
          <motion.h3 
            className="text-4xl text-[#051F20] text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('about.title')} <span className="text-[#235347]">{t('about.subtitle')}</span>
          </motion.h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone: any, idx: number) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50, rotateX: -20 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
                whileHover={{ 
                  y: -10,
                  scale: 1.05,
                  rotateX: 5
                }}
                style={{ 
                  perspective: 1000,
                  transformStyle: 'preserve-3d'
                }}
                className="relative bg-gradient-to-br from-[#DAF1DE] to-white p-8 rounded-3xl border-2 border-[#8EB69B]/30 hover:border-[#8EB69B]/60 hover:shadow-2xl transition-all duration-300"
              >
                <motion.div 
                  className="absolute -top-5 left-8 bg-gradient-to-r from-[#235347] to-[#8EB69B] text-white px-6 py-2 rounded-full shadow-xl"
                  whileHover={{ scale: 1.1, y: -2 }}
                  style={{ transform: 'translateZ(10px)' }}
                >
                  {milestone.year}
                </motion.div>
                
                <div className="mt-6">
                  <h4 className="text-[#051F20] text-xl mb-2">{milestone.title}</h4>
                  <p className="text-gray-600">{milestone.desc}</p>
                </div>
                
                {/* Decorative corner */}
                <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-[#8EB69B]/30 rounded-br-2xl"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: TrendingUp, title: "150+ Acres", desc: "Under professional management", color: "from-[#8EB69B] to-[#235347]" },
            { icon: Award, title: "50+ Farms", desc: "Successfully developed and transformed", color: "from-[#235347] to-[#163832]" },
            { icon: Target, title: "2024-2025", desc: "Rapid growth and expansion", color: "from-[#163832] to-[#0B2B26]" }
          ].map((achievement, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
              whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15, duration: 0.8 }}
              whileHover={{ 
                y: -15,
                rotateY: 10,
                scale: 1.05
              }}
              style={{ 
                perspective: 1200,
                transformStyle: 'preserve-3d'
              }}
              className="text-center bg-white p-12 rounded-3xl border-2 border-[#8EB69B]/10 hover:border-[#8EB69B]/40 shadow-xl hover:shadow-2xl transition-all duration-500 relative overflow-hidden"
            >
              {/* Animated background */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${achievement.color} opacity-0`}
                whileHover={{ opacity: 0.05 }}
                transition={{ duration: 0.3 }}
              />
              
              <motion.div 
                className={`h-24 w-24 bg-gradient-to-br ${achievement.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl relative overflow-hidden`}
                whileHover={{ 
                  rotate: 360,
                  scale: 1.1
                }}
                transition={{ duration: 0.8 }}
                style={{ transform: 'translateZ(30px)' }}
              >
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.2, 0.5, 0.2]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity
                  }}
                />
                <achievement.icon className="h-12 w-12 text-white relative z-10" />
              </motion.div>
              
              <h4 className="text-3xl text-[#051F20] mb-3">{achievement.title}</h4>
              <p className="text-gray-600 text-lg">{achievement.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}